// Power-ups: Lynreserve, Nødaflastning og Ring til Norge (DESIGN.md "Power-ups"). Hver har en
// varighed og en nedkøling i banetid. Effekten i flowet står i flow.ts og cities.ts via effects.ts.

import { CLOCK_SCALE } from './cities';
import { NORWAY_NODE } from './effects';
import { markDirty } from './flow';
import { degree, nodeById } from './grid';
import type { ActionKind, GridNode, PowerId, SimEvent, State } from './types';

export const POWER: Record<PowerId, { ticks: number; cooldown: number }> = {
  fcr: { ticks: 100, cooldown: 300 },
  shed: { ticks: 100, cooldown: 1200 },
  norway: { ticks: 200, cooldown: 600 },
};

/** Lynreserve: byer, hvis ur kører, får 5 s ekstra. */
export const FCR_CLOCK_BACK = 50 * CLOCK_SCALE;

export const POWER_IDS: PowerId[] = ['fcr', 'shed', 'norway'];

export function powerOf(kind: ActionKind): PowerId | null {
  if (kind === 'power_fcr') return 'fcr';
  if (kind === 'power_shed') return 'shed';
  if (kind === 'power_norway') return 'norway';
  return null;
}

export function powerKind(id: PowerId): ActionKind {
  return id === 'fcr' ? 'power_fcr' : id === 'shed' ? 'power_shed' : 'power_norway';
}

/** Klar: nedkølingen er ovre, og effekten kører ikke. */
export function powerReady(state: State, id: PowerId): boolean {
  const p = state.power[id];
  return state.clockTick >= p.readyAt && state.clockTick >= p.until;
}

/** Sekunder til power-up'en er klar igen (0 = klar). */
export function powerWaitS(state: State, id: PowerId): number {
  const p = state.power[id];
  return Math.max(0, Math.ceil((Math.max(p.readyAt, p.until) - state.clockTick) / 10));
}

/** Tjele, hvis den er med i banen og har mindst én hel ledning. */
export function norwayLanding(state: State): GridNode | undefined {
  const n = nodeById(state, NORWAY_NODE);
  if (!n || !n.active || degree(state, n.id) === 0) return undefined;
  return n;
}

/** Kan byen aflastes? Kun rigtige byer med forbrug lige nu. */
export function sheddable(n: GridNode): boolean {
  return n.kind === 'city' && n.active && !n.phantom && n.city !== undefined && n.city.demandMw > 0 && n.city.darkTicks === 0;
}

/** Brug en power-up. Kalderen har tjekket, at den er lovlig. */
export function usePower(state: State, id: PowerId, target: string, events: SimEvent[]): void {
  const p = state.power[id];
  const d = POWER[id];
  p.until = state.clockTick + d.ticks;
  p.readyAt = state.clockTick + d.cooldown;
  p.target = target;
  p.used++;
  if (id === 'fcr') {
    for (const n of state.nodes) {
      if (n.city && n.active && !n.phantom && n.city.clock > 0) n.city.clock = Math.max(0, n.city.clock - FCR_CLOCK_BACK);
    }
  } else if (id === 'shed') {
    const n = nodeById(state, target);
    if (n?.city) n.city.happyThisYear = false;
  }
  markDirty(state);
  events.push({ t: 'power_used', tick: state.tick, power: id, target, untilTick: state.tick + d.ticks });
}

/** Ét tick: meld når en effekt slutter, så flowet regnes om uden den. */
export function updatePowers(state: State, events: SimEvent[]): void {
  for (const id of POWER_IDS) {
    if (state.power[id].until === state.clockTick) {
      markDirty(state);
      events.push({ t: 'power_ended', tick: state.tick, power: id });
    }
  }
}
